import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withKeys as _withKeys, withCtx as _withCtx, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  ref: "outer",
  class: "memories-searchbar"
}
const _hoisted_2 = {
  key: 0,
  class: "searchbar-results"
}
const _hoisted_3 = { class: "icon" }
const _hoisted_4 = {
  key: 1,
  class: "empty"
}
const _hoisted_5 = {
  key: 2,
  class: "empty"
}
const _hoisted_6 = {
  key: 3,
  class: "empty"
}
const _hoisted_7 = { class: "icon" }

import { ref, computed, watch, onMounted, useTemplateRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import NcTextField from '@nextcloud/vue/components/NcTextField';
import NcPopover from '@nextcloud/vue/components/NcPopover';

import { config } from '@services/user-config';
import { windowDims } from '@services/viewport';
import { useRouteIs } from '@services/route-is';
import { t } from '@services/l10n';

import * as dav from '@services/dav';
import * as lens from '@services/lens';
import { isAbortError, useAbort } from '@services/utils/abort-vue';
import { RenewingTimeout } from '@services/utils/renewing-timeout';

import Fuse from 'fuse.js';

import MagnifyIcon from 'vue-material-design-icons/Magnify.vue';
import AlbumIcon from 'vue-material-design-icons/ImageAlbum.vue';
import LocationIcon from 'vue-material-design-icons/MapMarker.vue';
import TagIcon from 'vue-material-design-icons/Tag.vue';
import XImg from '@components/frame/XImg.vue';
import XLoadingIcon from '@components/XLoadingIcon.vue';

import type { ICluster } from '@typings';


export default /*@__PURE__*/_defineComponent({
  __name: 'Searchbar',
  props: {
    autoFocus: { type: Boolean, default: false }
  },
  emits: ["select"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const route = useRoute();
const router = useRouter();
const routeIs = useRouteIs();
const textFieldRef = useTemplateRef<any>('textField');

const prompt = ref(String());

// Popover can be hidden by clicking outside and
// so subsequent changes to prompt do not trigger
// it to show again. This flag is used to force it.
const pHidden = ref(false);

// Pending live lens navigation (debounced)
const lensTimer = new RenewingTimeout();

const clusters = ref<ICluster[] | null>(null);
const clustersLoad = ref(false);
const clusterIs = dav.clusterIs;
const clusterPreview = dav.getClusterPreview;
const clusterTarget = dav.getClusterLinkTarget;
const abort = useAbort();

onMounted(() => {
  syncPromptFromRoute();
  setTimeout(() => {
    syncPromptFromRoute();
    if (props.autoFocus) {
      textFieldRef.value?.focus();
    }
  }, 100); // wait for opacity transition
});

const shown = computed(() => {
  // Live search mode navigates directly; no popover needed
  return !pHidden.value && !!prompt.value.length;
});

const clustersResult = computed((): ICluster[] => {
  if (!prompt.value) return [];
  return clustersFuse.value.search(prompt.value, { limit: 6 }).map((r) => r.item);
});

const clustersFuse = computed(() => {
  return new Fuse(clusters.value ?? [], { keys: ['name', 'display_name'], threshold: 0.3 });
});

/** Lens backend available (daemon URL configured) */
const lensEnabled = computed((): boolean => {
  return !!config.lens_enabled;
});

/** Live lens search hijacks typing only on desktop timeline/search views */
const isLensLive = computed((): boolean => {
  return lensEnabled.value && (routeIs.Base || routeIs.Search) && !windowDims.isMobile;
});

/** Explicit lens entry for anywhere live search does not apply */
const showLensEntry = computed((): boolean => {
  return !!prompt.value && lensEnabled.value && !isLensLive.value;
});

const lensEntryText = computed((): string => {
  return t('memories', 'Find photos matching “{query}”', { query: prompt.value });
});

watch(prompt, (val: string) => {
  pHidden.value = false;
  if (val) {
    load(); // load clusters
  }

  // Queue lens search if route changed.
  if (lens.routeQueryText(route.query.q) !== val) {
    queueLensSearch();
  }
});

watch(
  () => route.query.q,
  () => {
    syncPromptFromRoute();
  },
);

function select() {
  prompt.value = String();
  emit('select');
}

async function load() {
  // Load all clusters that we can search in
  if (!clustersLoad.value) {
    clustersLoad.value = true;
    const signal = abort.signal;

    const noop = new Promise<ICluster[]>((r) => r([]));

    try {
      const results = await Promise.allSettled([
        config.recognize_enabled ? dav.getFaceList('recognize', { signal }) : noop,
        config.facerecognition_enabled ? dav.getFaceList('facerecognition', { signal }) : noop,
        config.places_gis > 0 ? dav.getPlaces({ covers: 0, signal }) : noop,
        config.systemtags_enabled ? dav.getTags({ signal }) : noop,
        config.albums_enabled ? dav.getAlbums({ signal }) : noop,
      ]);
      signal.throwIfAborted();

      // Ignore all errors and flatten
      clusters.value = results
        .flatMap((r) => (r.status === 'fulfilled' ? r.value : []))
        .filter((c) => !!(c.name || c.display_name));
    } catch (e) {
      if (isAbortError(e)) return;
      console.error(e);
    }
  }
}

/** Mirror ?q= into the box when on the search view (e.g. direct open). */
function syncPromptFromRoute() {
  const query = routeIs.Search ? lens.routeQueryText(route.query.q) : String();
  if (query !== prompt.value) prompt.value = query;
}

/** Open the search view for the current prompt */
function openSearch() {
  const q = prompt.value.trim();
  if (!q || !lensEnabled.value || isLensLive.value) return;
  lensTimer.clear();
  router.push({ name: 'search', query: { q } });
  prompt.value = q;
  pHidden.value = true;
  emit('select');
}

/** Live lens search on desktop timeline/search views */
function queueLensSearch() {
  if (!isLensLive.value) return;
  lensTimer.set(routeToLens, 500);
}

/** Run the pending live lens navigation */
function routeToLens() {
  if (!lensEnabled.value) return;
  if (!prompt.value) {
    if (!routeIs.Base) {
      router.replace({ name: 'timeline' });
    }
  } else {
    router.replace({
      name: 'search',
      query: {
        ...route.query,
        q: prompt.value,
      },
    });
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(NcPopover), {
      shown: shown.value,
      "no-focus-trap": true,
      onAfterHide: _cache[2] || (_cache[2] = ($event: any) => (pHidden.value = true))
    }, {
      trigger: _withCtx(({ attrs }) => [
        _createElementVNode("div", _normalizeProps(_guardReactiveProps(attrs)), [
          _createVNode(_unref(NcTextField), {
            ref: "textField",
            class: "text-field",
            modelValue: prompt.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((prompt).value = $event)),
            autocomplete: "off",
            "label-outside": true,
            label: _unref(t)('memories', 'Search your photos …'),
            placeholder: _unref(t)('memories', 'Search your photos …'),
            onKeydown: _withKeys(openSearch, ["enter"])
          }, {
            icon: _withCtx(() => [
              _createVNode(MagnifyIcon, { size: 16 })
            ]),
            _: 1
          }, 8, ["modelValue", "label", "placeholder"])
        ], 16)
      ]),
      default: _withCtx(() => [
        (!isLensLive.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              (showLensEntry.value)
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: "cluster",
                    onClick: openSearch
                  }, [
                    _createElementVNode("div", _hoisted_3, [
                      _createVNode(MagnifyIcon, { size: 22 })
                    ]),
                    _createTextVNode(" " + _toDisplayString(lensEntryText.value), 1)
                  ]))
                : _createCommentVNode("", true),
              (prompt.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_unref(t)('memories', 'Start typing to find photos and albums')), 1))
                : (!clusters.value && clustersLoad.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createVNode(XLoadingIcon, { class: "fill-block" })
                    ]))
                  : (clustersResult.value.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_unref(t)('memories', 'No results found')), 1))
                    : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(clustersResult.value, (cluster) => {
                return (_openBlock(), _createBlock(_component_router_link, {
                  class: "cluster",
                  to: _unref(clusterTarget)(cluster),
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (select()))
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_7, [
                      (_unref(clusterIs).album(cluster))
                        ? (_openBlock(), _createBlock(AlbumIcon, {
                            key: 0,
                            size: 22
                          }))
                        : (_unref(clusterIs).place(cluster))
                          ? (_openBlock(), _createBlock(LocationIcon, {
                              key: 1,
                              size: 22
                            }))
                          : (_unref(clusterIs).tag(cluster))
                            ? (_openBlock(), _createBlock(TagIcon, {
                                key: 2,
                                size: 22
                              }))
                            : (_unref(clusterIs).face(cluster))
                              ? (_openBlock(), _createBlock(XImg, {
                                  key: 3,
                                  src: _unref(clusterPreview)(cluster),
                                  class: "preview-image"
                                }, null, 8, ["src"]))
                              : (_openBlock(), _createBlock(MagnifyIcon, {
                                  key: 4,
                                  size: 22
                                }))
                    ]),
                    _createTextVNode(" " + _toDisplayString(cluster.display_name ?? cluster.name), 1)
                  ]),
                  _: 2
                }, 1032, ["to"]))
              }), 256))
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["shown"])
  ], 512))
}
}

})